import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Plus, RotateCcw, Copy } from "lucide-react";
import { toast } from "sonner";

const STORAGE_KEY = "troutstart_checklists_v1";

const loadAll = () => {
    try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    } catch {
        return {};
    }
};

export default function InteractiveChecklist({ checklist }) {
    const [state, setState] = useState(() => loadAll()[checklist.id] || { checked: {}, custom: [] });
    const [draft, setDraft] = useState("");

    useEffect(() => {
        const all = loadAll();
        all[checklist.id] = state;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
    }, [state, checklist.id]);

    const items = [...checklist.items, ...state.custom];
    const done = items.filter((_, i) => state.checked[i]).length;
    const pct = items.length ? Math.round((done / items.length) * 100) : 0;
    const R = 26;
    const CIRC = 2 * Math.PI * R;

    const toggle = (i) =>
        setState((s) => ({ ...s, checked: { ...s.checked, [i]: !s.checked[i] } }));

    const addItem = () => {
        const text = draft.trim();
        if (!text) return;
        setState((s) => ({ ...s, custom: [...s.custom, text] }));
        setDraft("");
        toast.success("Added to your kit list");
    };

    const reset = () => {
        setState({ checked: {}, custom: [] });
        toast("Checklist reset — fresh start");
    };

    const exportList = async () => {
        const text = `${checklist.title} — TroutStart.com\n` + items.map((it, i) => `${state.checked[i] ? "[x]" : "[ ]"} ${it}`).join("\n");
        try {
            await navigator.clipboard.writeText(text);
            toast.success("Checklist copied to clipboard");
        } catch {
            toast.error("Could not copy — select and copy manually");
        }
    };

    return (
        <div
            className="rounded-2xl border border-white/10 bg-[#111C2E] p-6 sm:p-8"
            data-testid={`checklist-${checklist.id}`}
        >
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h3 className="font-sans text-xl sm:text-2xl font-medium text-slate-100">{checklist.title}</h3>
                    <p className="mt-1 font-body text-sm text-slate-400">{checklist.subtitle}</p>
                </div>
                <div className="relative shrink-0" data-testid={`checklist-${checklist.id}-progress`}>
                    <svg width="64" height="64" viewBox="0 0 64 64" className="-rotate-90">
                        <circle cx="32" cy="32" r={R} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="5" />
                        <motion.circle
                            cx="32" cy="32" r={R} fill="none" stroke="#14B8A6" strokeWidth="5" strokeLinecap="round"
                            strokeDasharray={CIRC}
                            animate={{ strokeDashoffset: CIRC - (pct / 100) * CIRC }}
                            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                        />
                    </svg>
                    <span className="absolute inset-0 flex items-center justify-center font-mono text-xs text-teal">{pct}%</span>
                </div>
            </div>

            <ul className="mt-6 space-y-1">
                <AnimatePresence initial={false}>
                    {items.map((item, i) => {
                        const checked = !!state.checked[i];
                        return (
                            <motion.li
                                key={`${i}-${item}`}
                                layout
                                initial={{ opacity: 0, x: -12 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.3 }}
                            >
                                <button
                                    onClick={() => toggle(i)}
                                    data-testid={`checklist-${checklist.id}-item-${i}`}
                                    className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors duration-300 ${
                                        checked ? "bg-teal/10" : "hover:bg-white/5"
                                    }`}
                                >
                                    <span
                                        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-colors duration-300 ${
                                            checked ? "bg-teal border-teal" : "border-white/25"
                                        }`}
                                    >
                                        {checked && <Check size={13} strokeWidth={3.5} className="text-[#0B1320]" />}
                                    </span>
                                    <span
                                        className={`font-body text-sm sm:text-base transition-colors duration-300 ${
                                            checked ? "text-slate-500 line-through" : "text-slate-200"
                                        }`}
                                    >
                                        {item}
                                    </span>
                                </button>
                            </motion.li>
                        );
                    })}
                </AnimatePresence>
            </ul>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addItem()}
                    placeholder="Add your own item..."
                    data-testid={`checklist-${checklist.id}-add-input`}
                    className="flex-1 rounded-xl border border-white/10 bg-[#0B1320] px-4 py-2.5 font-body text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:border-teal/60 transition-colors"
                />
                <div className="flex gap-3">
                    <button
                        onClick={addItem}
                        data-testid={`checklist-${checklist.id}-add-button`}
                        className="flex items-center gap-2 rounded-xl bg-teal px-4 py-2.5 font-sans text-sm font-semibold text-[#0B1320] hover:bg-coral hover:text-white transition-colors duration-300"
                    >
                        <Plus size={16} /> Add
                    </button>
                    <button
                        onClick={exportList}
                        data-testid={`checklist-${checklist.id}-export-button`}
                        aria-label="Copy checklist"
                        className="flex items-center gap-2 rounded-xl border border-white/15 px-4 py-2.5 font-sans text-sm text-slate-300 hover:border-teal/50 hover:text-teal transition-colors duration-300"
                    >
                        <Copy size={15} />
                    </button>
                    <button
                        onClick={reset}
                        data-testid={`checklist-${checklist.id}-reset-button`}
                        aria-label="Reset checklist"
                        className="flex items-center gap-2 rounded-xl border border-white/15 px-4 py-2.5 font-sans text-sm text-slate-300 hover:border-coral/60 hover:text-coral transition-colors duration-300"
                    >
                        <RotateCcw size={15} />
                    </button>
                </div>
            </div>
        </div>
    );
}
